import { useQuery } from '@tanstack/react-query';
import { createLoanCrudHooks } from './lib/create-crud-hooks';
import { api } from '@/lib/api';
import type { SecurityItem, SecuritySummary } from '@/types';

// ── Input types ──────────────────────────────────────────

interface CreateSecurityInput {
  description: string;
  securityType: string;
  priority?: number;
  priorEncumbrances?: number;
  existingDebt?: number;
  existingDebtHolder?: string;
  borrowerValuation?: number;
  internalEstimate?: number;
  valuerEstimate?: number;
  valuationDate?: string;
  valuerName?: string;
  notes?: string;
  // Address fields (for REAL_PROPERTY)
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
}

interface UpdateSecurityInput {
  id: string;
  data: Partial<CreateSecurityInput & { status: string }>;
}

// ── Factory CRUD hooks ───────────────────────────────────

const { useList, useCreate, useUpdate, useDelete } = createLoanCrudHooks<
  SecurityItem,
  CreateSecurityInput,
  UpdateSecurityInput
>('securities', 'securities');

export function useSecurities(loanId: string) {
  return useList(loanId);
}

export function useCreateSecurity(loanId: string) {
  return useCreate(loanId);
}

export function useUpdateSecurity(loanId: string) {
  return useUpdate(loanId);
}

export function useDeleteSecurity(loanId: string) {
  return useDelete(loanId);
}

// ── Fetch security summary ───────────────────────────────

async function fetchSecuritySummary(loanId: string): Promise<SecuritySummary> {
  const { data } = await api.get<SecuritySummary>(`/loans/${loanId}/securities/summary`);
  return data;
}

export function useSecuritySummary(loanId: string) {
  return useQuery({
    queryKey: ['securities', loanId, 'summary'],
    queryFn: () => fetchSecuritySummary(loanId),
    enabled: !!loanId,
  });
}
